// The Formatting reference, which is the other half of R3 - "discovery without
// chrome".
//
// WHY IT IS A TABLE IN SETTINGS AND NOT A TOOLBAR. Markdown shorthand is depth
// with no surface: it costs nothing on the daily path and a stranger will never
// find it. This product's standing rule is general-audience defaults with depth
// reachable one level down and off the daily path (AGENTS.md), and Settings is
// where that level already is - beside the themes and the wording. The note's
// own placeholder (`NoteEditor.tsx`) names two shorthands where somebody is
// actually typing; this is the reference for when they want the rest.
//
// IT LIVES IN `notes/` AND NOT IN `shell/` on purpose. It describes the
// vocabulary `note-markdown.ts` parses and the shortcuts `note-typing.ts`
// applies, so a row here and the rule it describes are in the same directory and
// change together. Settings draws it; Settings does not own it.
//
// IT IS PLAIN IN BOTH VOICES and is deliberately not in `voice.ts`. Every line
// is a fact - `#` makes a heading whatever wording somebody chose - and a fact
// is identical in both columns (`shell/voice.ts`, boundary 3).

/** One row: what you type, what it does, and the key that types it for you. */
export interface FormatRow {
  syntax: string;
  means: string;
  /** The letter or key pressed with the modifier, or absent for typing only. */
  key?: string;
}

/**
 * Whether the current platform is macOS.
 *
 * A FUNCTION AND NOT A CONSTANT, so it is read when it is asked rather than
 * when the module is first imported. The formatting reference and the actual
 * shortcuts therefore always use the same platform answer.
 */
export function usesCommandKey(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /\bMacintosh\b/i.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) === 0;
}

/** React's synthetic event and the DOM's `KeyboardEvent` both satisfy this. */
export interface Chord {
  ctrlKey: boolean;
  metaKey: boolean;
}

/**
 * Whether a chord carries the modifier this platform names for application
 * shortcuts: Command on macOS, Control everywhere else.
 *
 * Bold, link and the view toggle use this rule. It leaves macOS Control+B,
 * Control+K and Control+E to the native text field bindings, and does not claim
 * Meta+B, Meta+K or Meta+E on platforms where Control is the advertised key.
 * Italic and task toggling deliberately accept either modifier and ask their
 * own narrower question in the editor.
 */
export function usesPrimaryModifier({ ctrlKey, metaKey }: Chord): boolean {
  return usesCommandKey() ? metaKey : ctrlKey;
}

/** `⌘` on macOS, or `Ctrl+` everywhere else. */
export function modifierLabel(): string {
  return usesCommandKey() ? '⌘' : 'Ctrl+';
}

/**
 * The whole typed-shorthand reference, in the order somebody meets it.
 *
 * WHAT IS NOT HERE IS THE POINT AS MUCH AS WHAT IS. No tables, callouts,
 * footnotes, `==highlight==`, `[[wikilinks]]` or `#tags`: each expands the
 * product's vocabulary beyond the accepted slice (`notes-formatting.md` §10).
 * Adding a row is a product decision and a parser change, in that order.
 */
export const FORMATTING: readonly FormatRow[] = [
  { syntax: '# Heading', means: 'A heading. ## is a smaller one.' },
  { syntax: '**bold**', means: 'Bold.', key: 'B' },
  { syntax: '*italic*', means: 'Italic.', key: 'I' },
  { syntax: '~~struck~~', means: 'Struck through.' },
  { syntax: '`code`', means: 'Code, inside a line.' },
  { syntax: '- item', means: 'A list. Enter starts the next one, Tab indents.' },
  { syntax: '1. item', means: 'A numbered list.' },
  { syntax: '- [ ] task', means: 'A checkbox.', key: 'Enter' },
  { syntax: '> quote', means: 'A quote.' },
  { syntax: '[words](url)', means: 'A link. Pasting one over a selection also works.', key: 'K' },
  { syntax: '```', means: 'A block of code, until the next ```.' },
  { syntax: '---', means: 'A line across the note.' },
];
